import apiClient from './client';

export interface ChurchPlanter {
  id: number;
  userId: number;
  name: string;
  spouseName?: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  location?: string;
  churchName?: string;
  supportingChurch?: string;
  status: 'prospect' | 'candidate' | 'active' | 'graduated' | 'inactive' | 'withdrawn';
  stage?: 'inquiry' | 'application' | 'assessment' | 'training' | 'planting' | 'established';
  pathKind?: 'growth' | 'planting' | null;
  plantingStep?: 'training' | 'funding' | 'post_funding' | null;
  effectivePlantingStep?: 'training' | 'funding' | 'post_funding' | null;
  trainingCompletedAt?: string | null;
  fundingEndDate?: string | null;
  journeyEndedAt?: string | null;
  journeyEndReason?: 'quit' | 'fired' | 'retired' | 'moved_on' | 'other' | string | null;
  journeyEndNotes?: string | null;
  assignedCoach?: number;
  assignedHub?: string;
  assignedHubLeader?: number;
  latitude?: number | null;
  longitude?: number | null;
  isActive: boolean;
  /** Computed from planting pipeline (funding / post-funding); not persisted */
  mbcbReportingEnabled?: boolean;
  onboardingCompleted: boolean;
  onboardingStep: number;
  /** Admin/staff: exclude from hub leader contact cadence tracking */
  hubContactTrackingExempt?: boolean;
  startDate?: string;
  graduationDate?: string;
  lastContactDate?: string;
  nextContactDate?: string;
  user?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
  coach?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  hubLeader?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PlanterFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stage?: string;
  pathKind?: string;
  plantingStep?: string;
  journeyEnded?: 'true' | 'false';
  journeyEndReason?: string;
  assignedHub?: string;
  assignedCoach?: number;
  sortBy?: string;
  order?: 'ASC' | 'DESC';
}

export interface UpdatePlanterData {
  name?: string;
  spouseName?: string;
  email?: string;
  phone?: string;
  secondaryPhone?: string;
  city?: string;
  state?: string;
  location?: string;
  churchName?: string;
  supportingChurch?: string;
  status?: string;
  stage?: string;
  assignedCoach?: number | null;
  assignedHub?: string | null;
  assignedHubLeader?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  mailingAddressStreet?: string;
  mailingAddressStreet2?: string;
  mailingAddressCity?: string;
  mailingAddressState?: string;
  mailingAddressZip?: string;
  mailingAddressCountry?: string;
  demographics?: any;
  ministry?: any;
  startDate?: string;
  graduationDate?: string;
  notes?: string;
  hubContactTrackingExempt?: boolean;
}

class PlanterService {
  async getAllPlanters(filters: PlanterFilters = {}) {
    const response = await apiClient.get('/planters', { params: filters });
    return response.data;
  }

  async getPlanter(id: number) {
    const response = await apiClient.get(`/planters/${id}`);
    return response.data;
  }

  async updatePlanter(id: number, data: UpdatePlanterData) {
    const response = await apiClient.put(`/planters/${id}`, data);
    return response.data;
  }

  async assignCoach(id: number, coachId: number) {
    const response = await apiClient.put(`/planters/${id}/assign-coach`, { coachId });
    return response.data;
  }

  async updateStage(id: number, stage: string) {
    const response = await apiClient.put(`/planters/${id}/stage`, { stage });
    return response.data;
  }

  async deletePlanter(id: number) {
    const response = await apiClient.delete(`/planters/${id}`);
    return response.data;
  }

  async getPlanterHistory(id: number) {
    const response = await apiClient.get(`/planters/${id}/history`);
    return response.data;
  }

  async getHubStats() {
    // This would need to be implemented on backend if needed
    // For now, we'll aggregate from planter data on frontend
    const response = await apiClient.get('/planters');
    return response.data;
  }

  async bulkGeocodePlanters() {
    const response = await apiClient.post('/geocoding/bulk-geocode-planters');
    return response.data;
  }
}

export const planterService = new PlanterService();
